import { ReactNode, useEffect, useState } from "react";
import { Search, X, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface ListToolbarProps {
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Number of selected rows; renders bulk action bar when > 0. */
  selectedCount?: number;
  bulkActions?: ReactNode;
  onClearSelection?: () => void;
  onExportCsv?: () => void;
  /** Filter chips / dropdowns. */
  filters?: ReactNode;
  /** Right-side actions (e.g. "New" button). */
  actions?: ReactNode;
}

export function ListToolbar({
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  selectedCount = 0,
  bulkActions,
  onClearSelection,
  onExportCsv,
  filters,
  actions,
}: ListToolbarProps) {
  // Debounce search input to avoid hammering the server.
  const [local, setLocal] = useState(search ?? "");
  useEffect(() => setLocal(search ?? ""), [search]);
  useEffect(() => {
    if (!onSearchChange) return;
    const id = setTimeout(() => {
      if (local !== (search ?? "")) onSearchChange(local);
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {onSearchChange && (
            <div className="relative w-full sm:w-72">
              <Search
                className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-8"
                aria-label="Search"
              />
            </div>
          )}
          {filters}
        </div>
        <div className="flex items-center gap-2">
          {onExportCsv && (
            <Button variant="outline" size="sm" onClick={onExportCsv}>
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          )}
          {actions}
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
          <span className="text-sm">
            {selectedCount} selected
          </span>
          <div className="flex items-center gap-2">
            {bulkActions}
            {onClearSelection && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearSelection}
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
